/**
 * Formatting Utilities
 *
 * Utilities for formatting dates, currency, and other display values.
 */

/**
 * Formats a date-only string (e.g. `"2026-02-09"`) for display in en-GB short form.
 * Formatting is fixed to UTC so the same calendar date renders regardless of the
 * caller's timezone — `new Date("YYYY-MM-DD")` parses as UTC midnight, and without
 * `timeZone: "UTC"` hosts west of UTC would roll back a day. Not intended for
 * datetime inputs where the user's local timezone is meaningful.
 * @param dateString - Date-only ISO string (`YYYY-MM-DD`)
 * @returns Formatted date string (e.g. `"09 Feb 2026"`)
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Formats a Date object to YYYY-MM-DD string without timezone conversion
 * @param date - Date object to format
 * @returns Formatted date string (e.g., "2026-02-09") or undefined if date is undefined
 */
export const formatDateToISO = (date: Date | undefined): string | undefined => {
  if (!date) {
    return undefined;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Safely parses a price value, returning 0 for NaN/null/undefined.
 * Unlike `Number(x) ?? 0`, this correctly handles NaN.
 */
export function parsePrice(price: string | number | null | undefined): number {
  if (price === undefined || price === null) {
    return 0;
  }
  const num = typeof price === "string" ? Number.parseFloat(price) : price;
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Formats a price value for display with currency symbol and ISO code.
 * Returns "-" for missing or unparseable values.
 */
export function formatPrice(price: string | number | null | undefined, currencySymbol = "$", currencyIso = "USD"): string {
  if (price === undefined || price === null) {
    return "-";
  }
  const numPrice = typeof price === "string" ? Number.parseFloat(price) : price;
  if (Number.isNaN(numPrice)) {
    return "-";
  }
  return `${currencySymbol}${numPrice.toFixed(2)} ${currencyIso}`;
}

/**
 * Formats a number as currency using US (en-US) locale formatting
 * @param amount - Amount to format
 * @param currency - Currency code (default: "USD")
 * @returns Formatted currency string with US number format (e.g., "$1,234.56" for USD, "€1,234.56" for EUR)
 * @note Uses en-US locale: comma (,) for thousands separator, period (.) for decimal separator, currency symbol before amount
 */
export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};
